// src/features/executive/insight/widgets/NewPatientTrendCard.tsx
import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import { useMemo, useState } from 'react'
import { Chart } from 'react-chartjs-2'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { formatDateThaiShort, formatVariance } from 'utils/executive.utils'
import { useNewPatientTrend } from '../hooks/use-new-patient-trend'
import type { NewPatientCountMode, NewPatientTrendGranularity } from 'api/executive/executive.types'

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, Legend, LineElement, PointElement)

// Peak callout only when a bucket stands out by ≥30% above the mean —
// otherwise it'd label "the highest of three nearly-equal months" which
// isn't actionable.
const PEAK_MIN_MULTIPLIER = 1.3
// First-half vs second-half thresholds. Need ≥4 buckets to have a meaningful
// midpoint split; otherwise hide the chip (random small samples are noisy).
const TREND_MIN_BUCKETS = 4
const TREND_RISING_RATIO = 1.15
const TREND_FALLING_RATIO = 0.85

const fmt = (n: number) => new Intl.NumberFormat('th-TH').format(Math.round(n || 0))

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

// Expand a chart bucket label (YYYY-MM or YYYY-MM-DD) to a full ISO range.
// YYYY-MM → first day to last day of that month.
// YYYY-MM-DD → same day (no-op).
function expandBucketRange(bucket: string): { start: string; end: string } {
  if (bucket.length === 7) {
    const [y, m] = bucket.split('-').map(Number)
    const last = new Date(y, m, 0).getDate()
    const mm = String(m).padStart(2, '0')
    const dd = String(last).padStart(2, '0')
    return { start: `${y}-${mm}-01`, end: `${y}-${mm}-${dd}` }
  }
  return { start: bucket, end: bucket }
}

export default function NewPatientTrendCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // Default to 'branch' — that's what counter staff use for branch KPIs.
  // Switching to 'organization' is for executives looking at true acquisition.
  const [countMode, setCountMode] = useState<NewPatientCountMode>('branch')
  const { data, loading, error, refetch } = useNewPatientTrend(countMode)

  const title = t('EXECUTIVE.INSIGHT.NEW_TREND.TITLE')
  const subtitleBase = t(
    countMode === 'branch'
      ? 'EXECUTIVE.INSIGHT.NEW_TREND.SUBTITLE_BRANCH'
      : 'EXECUTIVE.INSIGHT.NEW_TREND.SUBTITLE_ORG',
  )

  // Resolve the granularity unit text ("เดือน" / "สัปดาห์" / "วัน") for the
  // "เฉลี่ย Y/<unit>" hint. Defaults to "เดือน" if the field is missing
  // (older backend).
  const granularity: NewPatientTrendGranularity = data?.granularity ?? 'month'
  const unitLabel = t(`EXECUTIVE.INSIGHT.NEW_TREND.UNIT_${granularity.toUpperCase()}`, {
    defaultValue: granularity === 'day' ? 'วัน' : granularity === 'week' ? 'สัปดาห์' : 'เดือน',
  })

  const total = data?.total ?? 0
  const previousTotal = data?.previousTotal ?? null
  const avgPerBucket = data?.avgPerBucket ?? 0
  const previousAvgPerBucket = data?.previousAvgPerBucket ?? null
  const totalVariance = varianceOf(total, previousTotal)
  const avgVariance = varianceOf(avgPerBucket, previousAvgPerBucket)

  const isEmpty = !data || data.points.length === 0 || data.total === 0

  const handleCardClick = (e: React.MouseEvent) => {
    const tag = (e.target as HTMLElement).tagName
    if (tag === 'CANVAS' || tag === 'BUTTON') return
    dispatch(pushDrill({ id: 'new-patient-trend', label: title }))
  }

  const chartData = useMemo(() => {
    if (!data) return null
    const curData = data.points.map((p) => p.newCount)
    // Align prev by INDEX (position 1 cur ↔ position 1 prev). Pad with 0
    // when prev has fewer buckets (e.g. zero-count buckets get dropped
    // server-side). Truncate when prev has more.
    const prevData = (data.previousPoints ?? []).slice(0, data.points.length)
    while (prevData.length < data.points.length) prevData.push({ bucket: '', newCount: 0 })
    return {
      labels: data.points.map((p) => formatDateThaiShort(p.bucket)),
      datasets: [
        {
          type: 'bar' as const,
          label: t('EXECUTIVE.INSIGHT.NEW_TREND.COUNT'),
          data: curData,
          backgroundColor: '#2a8d9c',
          order: 2,
        },
        ...(previousTotal != null
          ? [{
              type: 'line' as const,
              label: t('EXECUTIVE.INSIGHT.NEW_TREND.PREV_PERIOD', { defaultValue: 'ช่วงก่อนหน้า' }),
              data: prevData.map((p) => p.newCount),
              borderColor: '#bdbdbd',
              borderDash: [4, 4],
              backgroundColor: 'transparent',
              borderWidth: 2,
              pointRadius: 2,
              pointBackgroundColor: '#bdbdbd',
              tension: 0,
              order: 1,
            }]
          : []),
      ],
    }
  }, [data, previousTotal, t])

  // Peak bucket — only callout if it stands out clearly above average.
  const peak = useMemo(() => {
    if (!data || data.points.length === 0 || avgPerBucket === 0) return null
    let maxIdx = 0
    for (let i = 1; i < data.points.length; i++) {
      if (data.points[i].newCount > data.points[maxIdx].newCount) maxIdx = i
    }
    const peakPoint = data.points[maxIdx]
    if (peakPoint.newCount < avgPerBucket * PEAK_MIN_MULTIPLIER) return null
    return {
      bucket: peakPoint.bucket,
      count: peakPoint.newCount,
      vsAvg: (peakPoint.newCount - avgPerBucket) / avgPerBucket,
    }
  }, [data, avgPerBucket])

  // Trend direction via first-half vs second-half average.
  const trend = useMemo(() => {
    if (!data || data.points.length < TREND_MIN_BUCKETS) return null
    const mid = Math.floor(data.points.length / 2)
    const first = data.points.slice(0, mid)
    const second = data.points.slice(mid)
    if (first.length === 0 || second.length === 0) return null
    const avgFirst = first.reduce((s, p) => s + p.newCount, 0) / first.length
    const avgSecond = second.reduce((s, p) => s + p.newCount, 0) / second.length
    if (avgFirst === 0) return null
    const ratio = avgSecond / avgFirst
    if (ratio >= TREND_RISING_RATIO) return { direction: 'up' as const, pct: ratio - 1 }
    if (ratio <= TREND_FALLING_RATIO) return { direction: 'down' as const, pct: ratio - 1 }
    return null
  }, [data])

  const toggle = (
    <Tooltip title={subtitleBase as string}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={countMode}
        onChange={(_e, v) => { if (v) setCountMode(v) }}
        sx={{
          '.MuiToggleButton-root': {
            padding: '2px 10px',
            fontSize: 11,
            textTransform: 'none',
            lineHeight: 1.5,
          },
        }}
      >
        <ToggleButton value="branch">{t('EXECUTIVE.INSIGHT.NEW_TREND.MODE_BRANCH')}</ToggleButton>
        <ToggleButton value="organization">{t('EXECUTIVE.INSIGHT.NEW_TREND.MODE_ORG')}</ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  )

  const subtitle = total > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
      <span>
        {t('EXECUTIVE.INSIGHT.NEW_TREND.SUBTITLE_STATS', '{{count}} คนไข้ใหม่ • เฉลี่ย {{avg}}/{{unit}}', {
          count: fmt(total),
          avg: fmt(avgPerBucket),
          unit: unitLabel,
        } as any)}
      </span>
      {totalVariance != null && (
        <Typography
          component="span"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
          color={totalVariance >= 0 ? 'success.main' : 'error.main'}
        >
          {totalVariance >= 0
            ? <TrendingUpIcon sx={{ fontSize: 13, mr: 0.25 }} />
            : <TrendingDownIcon sx={{ fontSize: 13, mr: 0.25 }} />}
          {formatVariance(totalVariance)}
        </Typography>
      )}
      {avgVariance != null && Math.abs(avgVariance) >= 0.1 && (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
        >
          · {t('EXECUTIVE.INSIGHT.NEW_TREND.AVG_DELTA', 'เฉลี่ย {{delta}} vs ช่วงก่อน', {
            delta: formatVariance(avgVariance),
          })}
        </Typography>
      )}
    </Box>
  ) : (
    subtitleBase
  )

  const notice = (peak || trend) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {peak && (
        <Chip
          icon={<StarOutlineIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.NEW_TREND.PEAK_CHIP',
                'Peak: {{bucket}} — {{count}} คน ({{delta}} จากเฉลี่ย)',
                {
                  bucket: peak.bucket,
                  count: fmt(peak.count),
                  delta: `+${(peak.vsAvg * 100).toFixed(0)}%`,
                } as any
              )}
            </Typography>
          }
          color="info"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {trend && trend.direction === 'up' && (
        <Chip
          icon={<TrendingUpIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.NEW_TREND.TREND_UP',
                'แนวโน้มเพิ่มขึ้น: ครึ่งหลังมากกว่าครึ่งแรก {{pct}}',
                { pct: formatVariance(trend.pct) }
              )}
            </Typography>
          }
          color="success"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {trend && trend.direction === 'down' && (
        <Chip
          icon={<TrendingDownIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.NEW_TREND.TREND_DOWN',
                'แนวโน้มลดลง: ครึ่งหลังน้อยกว่าครึ่งแรก {{pct}}',
                { pct: formatVariance(trend.pct) }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  return (
    <WidgetCard
      title={title}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.NEW_TREND.SOURCE_TOOLTIP',
        '"คนไข้ใหม่" = คนไข้ที่ MIN(orders.date) อยู่ในช่วงเวลาที่เลือก\n• Branch mode: นับครั้งแรกของแต่ละ (patientId × branchId) — รวมเคสหยิบแฟ้มข้ามสาขา (ที่สาขานี้ใหม่ แต่ไม่ใช่เครือใหม่)\n• Organization mode: นับครั้งแรกของ patientId ในเครือทั้งหมด — true acquisition\n\nGranularity ปรับอัตโนมัติ: ≤ 31 วัน = รายวัน, ≤ 90 วัน = รายสัปดาห์, เกินนั้น = รายเดือน\n\nเส้นประสีเทา = ช่วงเวลาก่อนหน้า (period-over-period overlay, จับคู่ตามลำดับ bucket)'
      )}
      subtitle={subtitle}
      headerAction={toggle}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.NEW_TREND.EMPTY',
        'ไม่พบคนไข้ใหม่ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      onRetry={refetch}
      onClick={handleCardClick}
      skeletonHeight={300}
    >
      <Box sx={{ height: 220 }}>
        <Chart
          type="bar"
          data={chartData as any}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const ds = ctx.dataset?.label || ''
                    const v = Number(ctx.parsed.y)
                    return `${ds}: ${fmt(v)}`
                  },
                },
              },
            },
            scales: {
              y: { beginAtZero: true, ticks: { precision: 0 } },
            },
            onClick: (_evt, elements) => {
              if (!elements || elements.length === 0) return
              // Only drill from the bar dataset (index 0); ignore line clicks
              if (elements[0].datasetIndex !== 0) return
              const bucket = data?.points[elements[0].index]?.bucket
              if (!bucket) return
              dispatch(pushDrill({
                id: 'new-patient-trend',
                label: `${title} · ${formatDateThaiShort(bucket)}`,
                scopedFilters: { dateRange: { preset: 'custom', ...expandBucketRange(bucket) } },
              }))
            },
          }}
        />
      </Box>
    </WidgetCard>
  )
}
